import { EventTypes, HISTORY_ACTION_MODE } from '@candulabs/craft';

import { ContentType, VariantId } from '@candu-ai/content/types';
import { CRAFT_ROOT_ID } from 'craft/constants';
import { useCraftPropsContext } from 'craft/contexts';
import { useEditor } from 'craft/hooks/useEditor';
import { InsertMode, useMutations } from 'craft/hooks/useMutations';
import { getFirstHotspot } from 'craft/resolvers/Hotspot/prototypes/FIRST_HOTSPOT';
import { getSubsequentHotspot } from 'craft/resolvers/Hotspot/prototypes/SUBSEQUENT_HOTSPOT';
import { getFirstTourStep } from 'craft/resolvers/TourStep/prototypes/FIRST_TOUR_STEP';
import { getSubsequentTourStep } from 'craft/resolvers/TourStep/prototypes/SUBSEQUENT_TOUR_STEP';
import { SerializedNodeTree } from 'craft/types';
import { TooltipType } from 'craft/types/TooltipType';

export interface AddFunctions {
  addFirst: () => void;
  addSubsequent: () => void;
}

/**
 * Common functionality for adding Tooltips
 */
export const useAddTooltipStep = (tooltipType: TooltipType, variant?: VariantId): AddFunctions => {
  const { insertSerializedNodeTrees } = useMutations();
  const { contentMetadata } = useCraftPropsContext();
  const { actions } = useEditor();

  const [FIRST, SUBSEQUENT] = (() => {
    switch (tooltipType) {
      case ContentType.HotspotGroup:
        return [
          getFirstHotspot({ contentName: contentMetadata?.name || 'Hotspot' }),
          getSubsequentHotspot({
            contentName: contentMetadata?.name || 'Hotspot',
            variantId: variant || 'base',
          }),
        ];
      case ContentType.Tour:
        return [
          getFirstTourStep({ contentName: contentMetadata?.name || 'Tour Step' }),
          getSubsequentTourStep({
            contentName: contentMetadata?.name || 'Tour Step',
            variantId: variant || 'base',
          }),
        ];
      default:
        return [null, null];
    }
  })();

  const addAndSelect = (serializedTree: SerializedNodeTree) => {
    const newTree = insertSerializedNodeTrees([serializedTree], CRAFT_ROOT_ID, InsertMode.INTO);
    actions.setNodeEvent(EventTypes.selected, newTree[0].rootNodeId, HISTORY_ACTION_MODE.MERGE);
  };

  const addFirst = () => {
    if (!FIRST) {
      return;
    }
    addAndSelect(FIRST);
  };

  const addSubsequent = () => {
    if (!SUBSEQUENT) {
      return;
    }
    addAndSelect(SUBSEQUENT);
  };

  return {
    addFirst,
    addSubsequent,
  };
};
